import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";

const paymentModes = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank", label: "Bank Transfer" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

const inputClass =
  "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full";

const labelClass = "text-xs font-medium text-slate-700";

const AdjustPaymentModal = ({ open, onClose, clientId, onSuccess, editData = null }) => {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("");
  const [note, setNote] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);

  // Sync state with editData
  useEffect(() => {
    if (open) {
      if (editData) {
        setAmount(editData.credit || "");
        setNote(editData.description || "");
        setPaymentDate(
          editData.createdAt
            ? new Date(editData.createdAt).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0]
        );
        setMode("cash"); // Default fallback
      } else {
        setAmount("");
        setNote("");
        setPaymentDate(new Date().toISOString().split("T")[0]);
        setMode("");
      }
    }
  }, [open, editData]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!editData && !mode) {
      toast.error("Please select payment mode");
      return;
    }

    try {
      setLoading(true);

      const todayStr = new Date().toISOString().split("T")[0];
      const finalPaymentDate = (paymentDate === todayStr) ? new Date().toISOString() : paymentDate;

      if (editData) {
        await apiClient.put(
          `/clients/ledger/${editData._id}`,
          {
            amount: Number(amount),
            note,
            paymentDate: finalPaymentDate,
          }
        );
        toast.success("Payment updated successfully");
      } else {
        await apiClient.post(
          `/clients/${clientId}/adjust-payment`,
          {
            amount: Number(amount),
            mode,
            note,
            paymentDate: finalPaymentDate,
          }
        );
        toast.success("Payment recorded successfully");
      }

      onSuccess?.();
      onClose();
      setAmount("");
      setMode("");
      setNote("");
      setPaymentDate(new Date().toISOString().split("T")[0]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save payment");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-2 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* HEADER */}
        <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
          <h2 className="text-lg font-semibold">
            {editData ? "Edit Payment" : "Record Payment (Adjustment)"}
          </h2>
          <button onClick={onClose} className="hover:rotate-90 transition-transform">
            <X size={20} />
          </button>
        </div>

        {/* BODY - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            {editData
              ? "Modify the amount, date, or note for this manually recorded payment."
              : "Use this when client gives money without any order reference (advance, late payment, or remaining amount)."}
          </p>

          <div className="flex flex-col gap-4">
            {/* Amount */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Amount Received *</label>
              <input
                type="number"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputClass}
                placeholder="Enter amount"
              />
            </div>

            {/* Payment Date */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Payment Date</label>
              <input
                type="date"
                value={paymentDate}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setPaymentDate(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Payment Mode (Only for new entries) */}
            {!editData && (
              <div className="flex flex-col gap-1">
                <label className={labelClass}>Payment Mode *</label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select payment mode</option>
                  {paymentModes.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Note */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Note (optional)</label>
              <textarea
                rows="3"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className={inputClass}
                placeholder="Example: Advance received via UPI"
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="bg-white hover:bg-slate-100 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-all shadow-md hover:shadow-lg active:scale-95 text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? "Saving..." : "Save Payment"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdjustPaymentModal;
